import { defineStore } from 'pinia'   
import { computed, ref } from 'vue'
import { guid } from '@progress/kendo-vue-common'
import { DEBUG_ENABLED } from '@/util'

type NotificationType = 'none' | 'success' | 'info' | 'error' | 'warning' 

export class NotificationDTO {
    private _id: any
    private _message: string
    private _type: NotificationType
    private _timeInMs: number
    private _isDebug: boolean
    private _actionLabel: string | null
    private _action: (() => void) | null

    constructor(message: string, type?: NotificationType, timeInMs?: number) {
        this._id = guid()
        this._message = message
        this._type = type ?? 'none'
        this._timeInMs = timeInMs ?? 8000
        this._isDebug = false
        this._actionLabel = null
        this._action = null
    }

    static createForDebug(message: string, type?: NotificationType, timeInMs?: number): NotificationDTO {
        const notification = new NotificationDTO(message, type, timeInMs)
        notification._isDebug = true
        return notification;
    }

    /** Attach an action link (e.g. "Undo") to the notification. The callback runs when the user clicks it. */
    withAction(label: string, action: () => void): NotificationDTO {
        this._actionLabel = label
        this._action = action
        return this
    }

    get id() {
        return this._id
    }

    get message() {
        return this._message
    }

    get type() {
        return this._type
    }

    get timeInMs() {
        return this._timeInMs
    }

    get isDebug() {
        return this._isDebug
    }

    get actionLabel() {
        return this._actionLabel
    }

    get action() {
        return this._action
    }
}

/** store for temporary notifications */
export const useNotificationStore = defineStore('notificationStore', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _notifications = ref<NotificationDTO[]>([])

    /********************************************************************************************************************
     GETTERS
    ********************************************************************************************************************/
    /** currently active language */
    const activeNotifications = computed(() => _notifications.value.filter(n => !n.isDebug));
    const debugNotifications = computed(() => _notifications.value.filter(n => n.isDebug));

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    function addNotification(notification: NotificationDTO) {
        if (!DEBUG_ENABLED && notification.isDebug) {
            return // skip
        }

        _notifications.value.push(notification)
        setTimeout(() => removeNotification(notification.id), notification.timeInMs)
    }

    function removeNotification(id: any) {
        _notifications.value = _notifications.value.filter(n => n.id != id)
    }

    return { activeNotifications, debugNotifications, addNotification, removeNotification }
})